import React, { useEffect, useMemo, useState } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  PreviewCard,
  RSelect,
} from "../../../components/Component";
import { Badge, Col, Row, Spinner } from "reactstrap";
import { api } from "../../../utils/api";
import { toast } from "react-toastify";

const RiskScore7dPage = () => {
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [groupBy, setGroupBy] = useState("site");
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [payload, setPayload] = useState(null);

  const siteOptions = useMemo(
    () => [{ value: null, label: "Tous les sites" }, ...sites.map((s) => ({ value: s.id, label: s.name }))],
    [sites]
  );

  const groupByOptions = useMemo(
    () => [
      { value: "site", label: "Par site" },
      { value: "team", label: "Par équipe (assigné)" },
    ],
    []
  );

  const getRiskLevelColor = (riskLevel) => {
    const map = { high: "danger", medium: "warning", low: "success" };
    return map[riskLevel] || "secondary";
  };

  const loadSites = async () => {
    try {
      const response = await api.getSites();
      setSites(Array.isArray(response) ? response : response?.data || []);
    } catch (error) {
      console.error("Erreur chargement sites:", error);
    }
  };

  const loadScores = async () => {
    setLoading(true);
    try {
      const params = {
        group_by: groupBy,
        limit: 30,
      };
      if (selectedSiteId) {
        params.site_id = selectedSiteId;
      }
      const response = await api.getRiskScore7d(params);
      setPayload(response || null);
    } catch (error) {
      console.error("Erreur chargement score 7j:", error);
      toast.error("Impossible de charger le module score de risque 7j.");
      setPayload(null);
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!payload?.scores || payload.scores.length === 0) {
      toast.warning("Aucune donnée à exporter.");
      return;
    }

    const headers = [
      "Group Type",
      "Group Label",
      "Risk Score 7d",
      "Risk Level",
      "Trend %",
      "Estimated Negative Events 7d",
      "Top Factors",
      "Recommended Action",
    ];

    const lines = payload.scores.map((row) =>
      [
        row.group_type || "",
        row.group_label || "",
        row.risk_score_7d ?? 0,
        row.risk_level || "",
        row.trend_percent_vs_previous_30d ?? 0,
        row.estimated_negative_events_next_7d ?? 0,
        Array.isArray(row.top_factors) ? row.top_factors.join(" | ") : "",
        row.recommended_action || "",
      ]
        .map((v) => `"${String(v).replace(/"/g, '""')}"`)
        .join(",")
    );

    const csv = [headers.join(","), ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `risk_score_7d_${groupBy}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadSites();
  }, []);

  useEffect(() => {
    loadScores();
  }, [groupBy, selectedSiteId]);

  return (
    <React.Fragment>
      <Head title="Score de risque 7j" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>Score de risque prédictif 7 jours</BlockTitle>
              <BlockDes className="text-soft">
                <p>Module dédié pour prioriser les sites/équipes à traiter cette semaine.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <div className="d-flex">
                <Button className="me-2" color="light" onClick={loadScores} disabled={loading}>
                  {loading ? (
                    <>
                      <Spinner size="sm" className="me-1" />
                      Actualisation...
                    </>
                  ) : (
                    <>
                      <Icon name="reload" className="me-1" />
                      Actualiser
                    </>
                  )}
                </Button>
                <Button color="primary" outline onClick={exportCsv} disabled={loading || !(payload?.scores || []).length}>
                  <Icon name="download" className="me-1" />
                  Export CSV
                </Button>
              </div>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <PreviewCard>
            <Row className="g-3">
              <Col md="4">
                <label className="form-label">Regroupement</label>
                <RSelect
                  options={groupByOptions}
                  value={groupByOptions.find((opt) => opt.value === groupBy)}
                  onChange={(opt) => setGroupBy(opt?.value || "site")}
                />
              </Col>
              <Col md="4">
                <label className="form-label">Site</label>
                <RSelect
                  options={siteOptions}
                  value={siteOptions.find((opt) => opt.value === selectedSiteId) || siteOptions[0]}
                  onChange={(opt) => setSelectedSiteId(opt?.value ?? null)}
                />
              </Col>
              <Col md="4" className="d-flex align-items-end">
                <span className="text-soft small">
                  Fenêtre d&apos;analyse: 30 jours · Lissage temporel activé
                </span>
              </Col>
            </Row>
          </PreviewCard>
        </Block>

        <Block>
          <Row className="g-gs">
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount">{payload?.summary?.total_groups ?? 0}</div>
                <div className="text-soft small">Groupes évalués</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-danger">{payload?.summary?.high_risk ?? 0}</div>
                <div className="text-soft small">Risque élevé</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-warning">{payload?.summary?.medium_risk ?? 0}</div>
                <div className="text-soft small">Risque moyen</div>
              </PreviewCard>
            </Col>
            <Col sm="6" lg="3">
              <PreviewCard>
                <div className="amount text-success">{payload?.summary?.low_risk ?? 0}</div>
                <div className="text-soft small">Risque faible</div>
              </PreviewCard>
            </Col>
          </Row>
        </Block>

        <Block>
          <PreviewCard>
            {loading ? (
              <div className="text-center py-4">
                <Spinner color="primary" />
              </div>
            ) : !payload?.scores || payload.scores.length === 0 ? (
              <div className="text-center py-4">
                <span className="text-soft">Aucun score disponible pour ce filtre.</span>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-ul mb-0">
                  <thead>
                    <tr>
                      <th>Site / Équipe</th>
                      <th className="text-end">Score 7j</th>
                      <th className="text-center">Niveau</th>
                      <th>Tendance</th>
                      <th className="text-end">Événements à risque (estimés)</th>
                      <th>Facteurs clés</th>
                      <th>Action recommandée</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payload.scores.map((item, index) => (
                      <tr key={`${item.group_type}-${item.group_id}-${index}`}>
                        <td>
                          <div className="fw-medium">{item.group_label || "N/A"}</div>
                          {item.group_code && <div className="small text-soft">{item.group_code}</div>}
                        </td>
                        <td className="text-end fw-bold">{item.risk_score_7d ?? 0}</td>
                        <td className="text-center">
                          <Badge color={getRiskLevelColor(item.risk_level)}>{item.risk_level || "unknown"}</Badge>
                        </td>
                        <td>
                          <span className={(item.trend_percent_vs_previous_30d || 0) > 0 ? "text-danger" : "text-success"}>
                            {(item.trend_percent_vs_previous_30d || 0) > 0 ? "+" : ""}
                            {item.trend_percent_vs_previous_30d ?? 0}%
                          </span>
                        </td>
                        <td className="text-end">{item.estimated_negative_events_next_7d ?? 0}</td>
                        <td className="small">
                          {Array.isArray(item.top_factors) && item.top_factors.length > 0
                            ? item.top_factors.join(" • ")
                            : "-"}
                        </td>
                        <td className="small">{item.recommended_action || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </PreviewCard>
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default RiskScore7dPage;
